/**
 * blueprintsApi.js — THE ONLY module allowed to name the blueprints endpoints.
 *
 * 2026-06-22 (FIX 9) — there were THREE copies of the blueprints fetch
 * (BlueprintContext, pages/BlueprintLibrary, pages/GuidePage). That duplication
 * WAS the cause of three separate faults:
 *   - the dashboard issuing 2x GET /api/blueprints and /guide issuing 6x,
 *   - one copy using bare `axios` (token attached, but no refresh queue) while
 *     another used the configured client,
 *   - two copies with no session gate at all, relying on the route guard
 *     redirecting to /login to avoid a 401.
 *
 * One module fixes all three: configured client (so the refresh queue applies),
 * an in-flight + per-user cache (so concurrent and sequential callers share one
 * request per load), and the session gate in one place.
 *
 * scripts/check-blueprints-single-source.js fails the build if any other file
 * names these endpoints.
 */
import axiosInstance from '../utils/axiosConfig';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/blueprints`;

export const hasBlueprintSession = () =>
  typeof window !== 'undefined' && !!window.localStorage.getItem('accessToken');

const currentUserKey = () =>
  (typeof window !== 'undefined' && window.localStorage.getItem('userId')) || 'anon';

/**
 * 2026-06-22 (FIX 9 follow-up) — the cache/in-flight holder lives on a runtime
 * global, not on this module's closure. A module-level `let` is per module
 * INSTANCE, and the lazy-loaded /guide route resolved to a different instance,
 * so /guide kept issuing its own request (measured 2-3 per load) while the
 * eagerly-loaded callers shared one. One holder means one request per load
 * across chunk boundaries.
 */
const HOLDER_KEY = '__kmBlueprintsApiState';
const store = (() => {
  const root = typeof globalThis !== 'undefined' ? globalThis : {};
  if (!root[HOLDER_KEY]) {
    root[HOLDER_KEY] = {
      allCache: null, allInFlight: null,
      workflowCache: null, workflowInFlight: null,
      categoriesCache: null, categoriesInFlight: null,
    };
  }
  return root[HOLDER_KEY];
})();

/** All blueprints for the tenant. One request per load, shared by every caller. */
export const getAllBlueprints = async ({ force = false } = {}) => {
  if (!hasBlueprintSession()) return [];
  const userKey = currentUserKey();
  if (!force && store.allCache && store.allCache.userKey === userKey) return store.allCache.blueprints;
  if (store.allInFlight) return store.allInFlight;

  store.allInFlight = axiosInstance
    .get(BASE)
    .then((res) => {
      const list = res.data?.blueprints || [];
      store.allCache = { userKey, blueprints: list };
      return list;
    })
    .finally(() => {
      store.allInFlight = null;
    });

  return store.allInFlight;
};

export const clearBlueprintCache = () => {
  store.allCache = null;
  store.workflowCache = null;
  store.categoriesCache = null;
};

export const getBlueprintWorkflow = async () => {
  if (!hasBlueprintSession()) return {};
  if (store.workflowCache) return store.workflowCache;
  if (store.workflowInFlight) return store.workflowInFlight;
  store.workflowInFlight = axiosInstance
    .get(`${BASE}/workflow`)
    .then((res) => {
      store.workflowCache = res.data?.phases || {};
      return store.workflowCache;
    })
    .finally(() => { store.workflowInFlight = null; });
  return store.workflowInFlight;
};

export const getBlueprintCategories = async () => {
  if (!hasBlueprintSession()) return [];
  if (store.categoriesCache) return store.categoriesCache;
  if (store.categoriesInFlight) return store.categoriesInFlight;
  store.categoriesInFlight = axiosInstance
    .get(`${BASE}/categories`)
    .then((res) => {
      store.categoriesCache = res.data?.categories || [];
      return store.categoriesCache;
    })
    .finally(() => { store.categoriesInFlight = null; });
  return store.categoriesInFlight;
};

export const getBlueprintById = async (id) => {
  if (!hasBlueprintSession() || !id) return null;
  const res = await axiosInstance.get(`${BASE}/${id}`);
  return res.data?.blueprint || null;
};

export const getBlueprintByRoute = async (route) => {
  if (!hasBlueprintSession() || !route) return null;
  const res = await axiosInstance.get(`${BASE}/context${route}`);
  return res.data?.blueprint || null;
};

/** Fire-and-forget view counter. Never surfaces an error to the caller. */
export const recordBlueprintView = (id) => {
  if (!hasBlueprintSession() || !id) return;
  axiosInstance.post(`${BASE}/${id}/view`).catch(() => {});
};
